import { formatDistance } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import uuid from "react-uuid";
import InlineSearch from "../../../../../shared/ui/inputs/search/InlineSearch";

type Props = {
  events: any,
  query: string,
  onChange: any,
  loading: boolean,
  selected: string,
  setSelected: any,
};

const LeftEventsBar = ({
  events, query, onChange,
  loading, selected, setSelected,
}: Props) => (
  <div className="w-full border-b border-gray-200 flex flex-col bg-white shadow-sm">
    {/* Header section with Events title and search */}
    <div className="sticky top-0 left-0 right-0 px-5 py-3 bg-white z-10 border-b border-gray-200">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-800">Events</h3>
        
        {/* Search input */}
        <div className="w-[250px]">
          <InlineSearch 
            border 
            value={query} 
            onChange={onChange} 
            className="w-full py-2"
          />
        </div>
      </div>
    </div>
    
    {/* Events list section */}
    <div className="flex-1 overflow-x-auto">
      {loading && (
        <div className="absolute z-[1] flex items-center justify-center inset-0 bg-gray-100/40 backdrop-blur-sm">
          <svg aria-hidden="true" className="mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-indigo-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
          </svg>
        </div>
      )}
      
      {/* Event items */}
      {events.length > 0 ? (
        <div className="flex flex-row gap-2 px-2 py-4 overflow-x-auto border-b border-gray-200 bg-gray-50">
          {events.map((event: any) => (
            <button 
              type="button" 
              key={uuid()} 
              className={`flex flex-col bg-white min-w-[180px] text-left px-4 py-3 rounded-lg border transition-colors duration-150 ${selected === event?.event_name ? "bg-purple-50 border-purple-500" : "hover:bg-primary-50 border-gray-200"}`} 
              onClick={() => { setSelected(event?.event_name); }}
            >
              <div className="flex justify-between items-center w-full mb-2">
                <h5 className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                  {event?.event_name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                </h5>
                <div className="min-w-[20px] h-[20px] px-2 text-xs flex items-center justify-center rounded-full bg-purple-100 text-purple-700 font-medium">
                  {event?.occurances}
                </div>
              </div>
              <h6 className="text-xs text-gray-500">
                Last seen {formatDistance(zonedTimeToUtc(new Date((event?.last_occured?.replace(" ", "T"))), "UTC"), new Date(), { addSuffix: true })}
              </h6>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-5 text-center">
          <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 font-medium mb-1">No events found</p>
          <p className="text-gray-400 text-sm">Try changing the time window to see more events</p>
        </div>
      )}
    </div>
  </div>
);

export default LeftEventsBar;
